import React, { useMemo, useRef } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { useTranslation } from 'react-i18next'
import { PrimaryButton } from 'components/Shared/PrimaryButton'
import { Form, Formik } from 'formik'
import { FieldComponent } from 'components/Shared/FieldComponent'
import {
  momentToISOLocalDate,
  momentToISOLocalTime,
  parseFormDisplayedDateFormatWithZone,
  toDisplayDateTime,
  toISOOffsetLocalDateTime
} from 'utils/date'
import { triggerFormSubmit } from 'utils/utils'
import {
  ApproveCargoSchema,
  DeliveryPointCargoApproveForm
} from 'components/Order/OrderDetails/DeliveryPointCargoApproveForm'
import {
  isLoaded,
  isUnloaded,
  mapToCargoInitialValues
} from 'components/Order/OrderDetails/DeliveryPointCargoApproveForm/deliveryPointCargoHelper'
import styles from './HistoryTabs.module.css'
import DialogContentText from '@material-ui/core/DialogContentText'
import { ControlledSelectField } from 'components/Shared/ControlledSelectField'
import { OrderTimeZones } from 'components/Order/constants'
import { DatePickerFormFieldInline } from 'components/Shared/DatePickerInline'
import { createUpdateStatusChangeSchema } from '../StatusChangeDialog/schema'

export default function UpdateStatusChangeDialog ({ statusChange, previousStatusChange, order, onClose }) {
  const { t } = useTranslation()

  const formRef = useRef()

  const isOpen = !!statusChange

  const initialFormValues = useMemo(() => {
    const cargoInitialValues = statusChange.deliveryPointCargo ? mapToCargoInitialValues(statusChange) : {}

    const timeZone = (statusChange && statusChange.timeZone !== 'UTC') ? statusChange.timeZone : '+00:00'
    const parsedCreationDateTime = toISOOffsetLocalDateTime(toDisplayDateTime(statusChange.creationDateTime), timeZone)

    return {
      ...cargoInitialValues,
      date: momentToISOLocalDate(parsedCreationDateTime),
      time: momentToISOLocalTime(parsedCreationDateTime),
      timeZone: timeZone,
      cancellationCause: statusChange.cancellationCause,
      rejectionCause: statusChange.rejectionCause
    }
  }, [statusChange])

  const handleDismiss = () => {
    onClose(null)
  }

  function handleFormSubmit ({ date, time, timeZone, ...values }) {
    const creationDateTime = parseFormDisplayedDateFormatWithZone(date, time, timeZone)

    onClose({
      ...values,
      creationDateTime,
      timeZone
    })
  }

  const showTextCancellationField = useMemo(() => {
    return statusChange.status === 'CANCELLED'
  }, [statusChange])

  const showTextRejectionField = useMemo(() => {
    return statusChange.status === 'REJECTED'
  }, [statusChange])

  const showCargoFields = useMemo(() => {
    return statusChange.approved && statusChange.status && (isLoaded(statusChange) || isUnloaded(statusChange))
  }, [statusChange])

  const getValidationSchema = () => {
    const updateStatusChangeSchema = createUpdateStatusChangeSchema(formRef, previousStatusChange?.creationDateTime)
    return statusChange.deliveryPointCargo ? updateStatusChangeSchema.concat(ApproveCargoSchema) : updateStatusChangeSchema
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleDismiss}
      maxWidth={'md'}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContentText className={styles.contentContainer}>
        {t('order.details.history.actions.edit')}
      </DialogContentText>
      <DialogContent>
        <div className={'p-3'}>
          <Formik
            innerRef={formRef}
            initialValues={initialFormValues}
            enableReinitialize
            validationSchema={getValidationSchema()}
            onSubmit={values => handleFormSubmit(values)}>
            {
              () => (
                <Form>
                  <div className={'m-auto text-center'}>
                    <div className={styles.dateTimeContainer}>
                      <div>
                        <DatePickerFormFieldInline
                          name={`date`}
                          label={'form.orderStatusChange.date'}
                          skipIcon
                          datePickerClassName={styles.dateField}
                          notRequiredTouchedToError/>
                        <FieldComponent
                          fieldName={`time`}
                          label={'form.orderStatusChange.time'}
                          skipErrorIcon
                          className={styles.timeField}
                          requireTouchToShowError={false}
                        />
                      </div>
                      <ControlledSelectField
                        disableClear
                        options={OrderTimeZones}
                        fieldName="timeZone"
                        label={t('form.orderStatusChange.timeZone')}
                      />
                      {showTextCancellationField && <FieldComponent fieldName={'cancellationCause'}
                                                                    label={'form.orderStatusChange.cancellationCause'}
                                                                    multiline/>
                      }
                      {showTextRejectionField && <FieldComponent fieldName={'rejectionCause'}
                                                                 label={'form.orderStatusChange.rejectionCause'}
                                                                 multiline/>
                      }
                    </div>
                    {showCargoFields && <DeliveryPointCargoApproveForm order={order}
                                                                       action={statusChange}/>
                    }
                  </div>
                </Form>
              )
            }
          </Formik>
        </div>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          onClick={handleDismiss}
          label={'notifications.back'}
        />
        <PrimaryButton
          onClick={triggerFormSubmit(formRef)}
          label="notifications.confirm"
        />
      </DialogActions>
    </Dialog>
  )
}
